import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIdleExempt } from "../hooks/useIdleExempt";
import axios from "axios";
import { Building2, Plus, Pencil, Power, PowerOff, Search, X, Loader2, Save, ArrowRightLeft, ChevronRight, Trash2, AlertTriangle, Undo2 } from "lucide-react";
import { fmtDate, fmtCurrency } from "../lib/formatters";
import { useAuth, isPlatformAdmin } from "../contexts/AuthContext";
import { retryOnReloadHiccup } from "../utils/retryOnReloadHiccup";
import { TierChip } from "./AssociationsAdminScreen";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Slice 4 — Firms (Tenants) admin screen
//
// Access: Platform Administrator only. Anyone else gets a small "insufficient
// permissions" panel instead of the table.
//
// Actions: List / Create / Edit / Deactivate / Reactivate. The list defaults
// to showing everything (active + inactive) so admins can restore a firm.
// ---------------------------------------------------------------------------

const EMPTY_FORM = {
  name: "",
  legal_name: "",
  primary_contact: "",
  email: "",
  phone: "",
  address: "",
  website: "",
  tax_id: "",
  seats: "",
  currency: "USD",
  timezone: "America/Chicago",
  fiscal_year_start_month: 1,
  subscription_start_date: "",
  subscription_end_date: "",
  notes: "",
};

function FirmModal({ mode, initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM, ...(initial || {}) }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // 2026-03-05 (Dave) — Seats renders as `nn,nnn` on blur, raw digits
  // on focus so typing stays natural. Empty string = "unlimited"
  // and is preserved through the toggle so we never inject a `0`.
  const [seatsFocused, setSeatsFocused] = useState(false);

  const isEdit = mode === "edit";
  useIdleExempt(!isEdit);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      // 2026-02-25 (Dave) — PATCH-vs-POST payload shaping.
      //
      //   • CREATE (POST): omit empty strings so the backend doesn't
      //     store "" for optional fields that were never filled in.
      //   • EDIT (PATCH): send explicit `null` for empty strings so
      //     the backend knows the user CLEARED the field. Previously
      //     we skipped empty strings on both paths, which made it
      //     impossible to null out an already-populated optional
      //     field (e.g., Primary Contact Name) — the API would
      //     interpret "field not sent" as "no change".
      //
      // Date fields still get null (not "") when empty since the
      // backend's `_parse_date` treats null as "no change" and "" as
      // an invalid date.
      const payload = {};
      const DATE_FIELDS = new Set(["subscription_start_date", "subscription_end_date"]);
      const REQUIRED_ON_EDIT = new Set(["name"]);
      for (const [k, v] of Object.entries(form)) {
        const isEmpty = v === "" || v === undefined || v === null;
        if (isEmpty) {
          if (isEdit && !REQUIRED_ON_EDIT.has(k)) {
            // Signal an explicit clear.
            payload[k] = null;
          }
          // On CREATE, skip empty fields entirely.
          continue;
        }
        if (k === "seats") payload[k] = parseInt(v, 10) || null;
        else if (k === "fiscal_year_start_month") payload[k] = parseInt(v, 10) || 1;
        else if (DATE_FIELDS.has(k)) payload[k] = v;
        else payload[k] = v;
      }
      const res = isEdit
        ? await axios.patch(`${API}/admin/firms/${initial.tenant_id}`, payload)
        : await axios.post(`${API}/admin/firms`, payload);
      onSaved(res.data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(15,42,68,0.55)" }}
      data-testid="firm-modal"
      onClick={onClose}
    >
      <div
        className="bg-white border-2 border-navy w-[720px] max-h-[85vh] overflow-y-auto shadow-2xl rounded-[8px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-4 py-2.5 border-b border-rule"
          style={{ borderBottomWidth: 2, borderBottomColor: "#1C4E80" }}
        >
          <div className="flex items-center gap-2">
            <Building2 size={14} className="text-navy" />
            <div className="text-[11px] tracking-[0.14em] uppercase font-bold text-navy">
              {isEdit ? "Edit Firm" : "Add Firm"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-navy-wash rounded-[3px]"
            data-testid="firm-modal-close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="Firm Name" required>
            <input
              data-testid="firm-form-name"
              value={form.name}
              onChange={set("name")}
              className="u-input"
              autoFocus
            />
          </Field>
          <Field label="Legal Name">
            <input value={form.legal_name} onChange={set("legal_name")} className="u-input" />
          </Field>
          <Field label="Primary Contact">
            <input value={form.primary_contact} onChange={set("primary_contact")} className="u-input" />
          </Field>
          <Field label="Email">
            <input
              type="email"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              value={form.email}
              onChange={set("email")}
              className="u-input"
              data-testid="firm-form-email"
            />
          </Field>
          <Field label="Phone">
            <input value={form.phone} onChange={set("phone")} className="u-input" />
          </Field>
          <Field label="Website">
            <input value={form.website} onChange={set("website")} className="u-input" />
          </Field>
          <Field label="Address" span={2}>
            <input value={form.address} onChange={set("address")} className="u-input" />
          </Field>
          <Field label="Tax ID">
            <input value={form.tax_id} onChange={set("tax_id")} className="u-input" />
          </Field>
          <Field label="Seats">
            <input
              type="text"
              inputMode="numeric"
              value={
                seatsFocused || form.seats === "" || form.seats === null || form.seats === undefined
                  ? (form.seats ?? "")
                  : Number(form.seats).toLocaleString("en-US")
              }
              onFocus={() => setSeatsFocused(true)}
              onBlur={() => setSeatsFocused(false)}
              onChange={(e) => {
                // Strip commas + whitespace; accept digits only.
                const raw = e.target.value.replace(/[,\s]/g, "");
                if (raw === "" || /^\d+$/.test(raw)) {
                  setForm((f) => ({ ...f, seats: raw }));
                }
              }}
              className="u-input tabular-nums text-right"
              data-testid="firm-form-seats"
            />
          </Field>
          <Field label="Currency">
            <input value={form.currency} onChange={set("currency")} className="u-input" />
          </Field>
          <Field label="Time Zone">
            <input value={form.timezone} onChange={set("timezone")} className="u-input" />
          </Field>
          <Field label="Fiscal Year Start Month">
            <input
              type="number" min={1} max={12}
              value={form.fiscal_year_start_month}
              onChange={set("fiscal_year_start_month")}
              className="u-input"
            />
          </Field>
          <Field label="Subscription Start">
            <input type="date" value={form.subscription_start_date} onChange={set("subscription_start_date")} className="u-input" />
          </Field>
          <Field label="Subscription End">
            <input type="date" value={form.subscription_end_date} onChange={set("subscription_end_date")} className="u-input" />
          </Field>
          <Field label="Notes" span={2}>
            <textarea
              value={form.notes}
              onChange={set("notes")}
              rows={2}
              className="u-input resize-none"
            />
          </Field>
        </div>

        {error && (
          <div
            className="mx-5 mb-3 px-3 py-2 border border-coral bg-[#FEF5F2] text-coral text-[11px]"
            data-testid="firm-modal-error"
          >
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 px-5 pb-4">
          <button
            onClick={onClose}
            className="border border-rule bg-white hover:border-navy px-3 py-1.5 text-[11px] font-semibold text-slate hover:text-navy"
            data-testid="firm-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || !form.name.trim()}
            className="flex items-center gap-1.5 bg-navy text-white hover:bg-[#12406B] px-3 py-1.5 text-[11px] font-semibold rounded-[3px] disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ boxShadow: saving || !form.name.trim() ? "none" : "0 0 0 2px #EA6A47" }}
            data-testid="firm-modal-save"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {isEdit ? "Save Changes" : "Create Firm"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, span = 1, children }) {
  return (
    <label
      className={`flex flex-col gap-1 ${span === 2 ? "col-span-2" : ""}`}
      style={{ minWidth: 0 }}
    >
      <span className="text-[9.5px] tracking-[0.12em] uppercase font-bold text-slate">
        {label}{required && <span className="text-coral"> *</span>}
      </span>
      {children}
    </label>
  );
}

function CreateAssociationForm({ firm, onCancel, onCreated }) {
  useIdleExempt(true);
  // 2026-03-03 (Dave) — Anchor Plan assumption fields are pre-filled from
  // Platform Settings (`new_assoc_target_pct` / _inflation_rate /
  // _interest_rate / _year1_adjustment_pct) so a CPA sees the defaults
  // that will actually seed the Anchor scenario. Values are stored as
  // integer percent in settings (e.g. 40) and converted to the engine's
  // decimal contract (0.40) for both the form state and the create
  // payload. The user can still override any of the four before submit.
  const [form, setForm] = useState({
    name: "",
    tenant_display_id: "",
    address: "",
    unit_count: 0,
    current_fund_balance: 0,
    default_target_pct: 0,
    default_inflation_rate: 0,
    default_interest_rate: 0,
    default_adjustment_pct: 0,
    tier: "pro",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Load Anchor Plan defaults from Platform Settings once on mount. Falls
  // back to the hard-coded (40/4/3/0) preset if the fetch fails so the
  // drawer never renders blanks.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/platform-settings`);
        const s = res?.data?.settings || {};
        if (cancelled) return;
        setForm((prev) => ({
          ...prev,
          default_target_pct:
            (Number(s.new_assoc_target_pct) || 40) / 100,
          default_inflation_rate:
            (Number(s.new_assoc_inflation_rate) || 4) / 100,
          default_interest_rate:
            (Number(s.new_assoc_interest_rate) || 3) / 100,
          default_adjustment_pct:
            (Number(s.new_assoc_year1_adjustment_pct) || 0) / 100,
        }));
      } catch {
        // Preserve the initial zeros — Platform Admin can still edit.
      } finally {
        if (!cancelled) setSettingsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 2026-08-18 (Dave) — currency-format the Opening Balance field:
  // show `$1,234` on blur, raw digits on focus so it stays type-able.
  const [obFocused, setObFocused] = useState(false);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) {
      setError("Association name is required."); return;
    }
    setSaving(true); setError(null);
    try {
      const res = await axios.post(`${API}/admin/firms/${firm.tenant_id}/associations`, {
        name: form.name.trim(),
        tenant_display_id: form.tenant_display_id.trim() || undefined,
        address: form.address.trim() || undefined,
        unit_count: Number(form.unit_count) || 0,
        current_fund_balance: Number(form.current_fund_balance) || 0,
        default_target_pct: Number(form.default_target_pct) || 0,
        default_inflation_rate: Number(form.default_inflation_rate) || 0,
        default_interest_rate: Number(form.default_interest_rate) || 0,
        default_adjustment_pct: Number(form.default_adjustment_pct) || 0,
        tier: form.tier || "pro",
      });
      // Slice 5e (2026-02-24) — stay in the drawer. Let the parent refresh
      // its rows + the firms table so counts stay in sync. Full page
      // reloads only happen on reassign (where App-level state must change).
      onCreated && onCreated(res.data);
      // Slice 5h (2026-02-25) — new association must show in the TopBar /
      // Dashboard picker immediately.
      try {
        window.dispatchEvent(new CustomEvent("ureserve:associations-changed"));
      } catch { /* no-op */ }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setSaving(false);
    }
  };

  return (
    <div className="border border-navy bg-navy-wash/30 px-3 py-2 mb-2" data-testid="drawer-create-form">
      <div className="text-[10px] tracking-[0.12em] uppercase font-bold text-navy mb-1.5">
        New Association
      </div>
      {error && (
        <div className="px-2 py-1 border border-coral bg-[#FEF5F2] text-coral text-[11px] mb-2">
          {error}
        </div>
      )}
      <div className="grid grid-cols-2 gap-1.5 text-[11px]">
        <label className="col-span-2 flex flex-col gap-0.5">
          <span className="text-[9.5px] tracking-wide uppercase text-slate">Name *</span>
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className="border border-rule px-2 py-1 outline-none focus:border-navy"
            data-testid="drawer-create-name"
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9.5px] tracking-wide uppercase text-slate">Display ID</span>
          <input
            value={form.tenant_display_id}
            onChange={(e) => set("tenant_display_id", e.target.value)}
            placeholder="e.g. 110"
            className="border border-rule px-2 py-1 outline-none focus:border-navy font-mono"
            data-testid="drawer-create-display-id"
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9.5px] tracking-wide uppercase text-slate">Members / Units</span>
          <input
            type="number" min="0"
            value={form.unit_count}
            onChange={(e) => set("unit_count", e.target.value)}
            className="border border-rule px-2 py-1 outline-none focus:border-navy tabular-nums text-right"
            data-testid="drawer-create-units"
          />
        </label>
        <label className="col-span-2 flex flex-col gap-0.5">
          <span className="text-[9.5px] tracking-wide uppercase text-slate">Address</span>
          <input
            value={form.address}
            onChange={(e) => set("address", e.target.value)}
            className="border border-rule px-2 py-1 outline-none focus:border-navy"
            data-testid="drawer-create-address"
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9.5px] tracking-wide uppercase text-slate">Opening Balance</span>
          <input
            type="text"
            inputMode="numeric"
            value={
              obFocused || form.current_fund_balance === "" || form.current_fund_balance === 0
                ? form.current_fund_balance
                : fmtCurrency(Number(form.current_fund_balance) || 0, 0)
            }
            onFocus={() => setObFocused(true)}
            onBlur={() => setObFocused(false)}
            onChange={(e) => {
              // Accept digits + optional leading `-`; strip $ + commas.
              const raw = e.target.value.replace(/[$,\s]/g, "");
              // Allow only digits and one leading minus.
              if (raw === "" || raw === "-" || /^-?\d+$/.test(raw)) {
                set("current_fund_balance", raw);
              }
            }}
            className="border border-rule px-2 py-1 outline-none focus:border-navy tabular-nums text-right"
            data-testid="drawer-create-balance"
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9.5px] tracking-wide uppercase text-slate">Target %</span>
          <input
            type="number" step="0.01" min="0" max="1"
            value={form.default_target_pct}
            onChange={(e) => set("default_target_pct", e.target.value)}
            className="border border-rule px-2 py-1 outline-none focus:border-navy tabular-nums text-right"
            data-testid="drawer-create-target"
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9.5px] tracking-wide uppercase text-slate">Inflation %</span>
          <input
            type="number" step="0.001"
            value={form.default_inflation_rate}
            onChange={(e) => set("default_inflation_rate", e.target.value)}
            className="border border-rule px-2 py-1 outline-none focus:border-navy tabular-nums text-right"
            data-testid="drawer-create-inflation"
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9.5px] tracking-wide uppercase text-slate">Interest %</span>
          <input
            type="number" step="0.001"
            value={form.default_interest_rate}
            onChange={(e) => set("default_interest_rate", e.target.value)}
            className="border border-rule px-2 py-1 outline-none focus:border-navy tabular-nums text-right"
            data-testid="drawer-create-interest"
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9.5px] tracking-wide uppercase text-slate">Year 1 Adjustment %</span>
          <input
            type="number" step="0.001"
            value={form.default_adjustment_pct}
            onChange={(e) => set("default_adjustment_pct", e.target.value)}
            className="border border-rule px-2 py-1 outline-none focus:border-navy tabular-nums text-right"
            data-testid="drawer-create-adjustment"
          />
        </label>
        <label className="flex flex-col gap-0.5">
          {/* 2026-03-04 (Dave) — Per-association content tier. Drives
              which optional tabs render for users viewing this
              association (Version Content card in Platform Settings
              controls the actual tab list). Defaults to Pro. */}
          <span className="text-[9.5px] tracking-wide uppercase text-slate">Tier</span>
          <select
            value={form.tier || "pro"}
            onChange={(e) => set("tier", e.target.value)}
            className="border border-rule px-2 py-1 outline-none focus:border-navy"
            data-testid="drawer-create-tier"
          >
            <option value="pro">Pro</option>
            <option value="basic">Basic</option>
          </select>
        </label>
      </div>
      <div className="flex items-center justify-end gap-1.5 mt-2">
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-2 py-1 text-[11px] text-slate hover:text-navy"
          data-testid="drawer-create-cancel"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={saving || !form.name.trim()}
          className="px-2 py-1 text-[11px] font-semibold bg-navy text-white hover:bg-[#12406B] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="drawer-create-submit"
        >
          {saving ? <Loader2 size={11} className="animate-spin" /> : "Create"}
        </button>
      </div>
      <div className="text-[9.5px] text-slate mt-1.5">
        Under <b>{firm?.name}</b>. Starter plan year + Element Baseline are auto-created.
      </div>
    </div>
  );
}


function EditAssociationForm({ assoc, onCancel, onSaved }) {
  // Slice 5c (2026-02-24) — inline edit of an association's core fields.
  // Partial PATCH — only changed keys are sent.
  //
  // 2026-08-04 (Dave §4b) — Plan Year + Opening Fund added. These two live
  // on child rows (PlanYear.year, Scenario.beginning_balance) so we fetch
  // the full association bundle on mount to populate their initial values.
  const [form, setForm] = useState({
    name: assoc.name || "",
    tenant_display_id: assoc.tenant_display_id || "",
    address: assoc.address || "",
    unit_count: assoc.unit_count ?? 0,
    study_year: "",
    opening_balance: "",
    tier: assoc.tier || "pro",
    is_active: !!assoc.is_active,
  });
  const [initialChildFields, setInitialChildFields] = useState({
    study_year: "",
    opening_balance: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loadingBundle, setLoadingBundle] = useState(true);
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/associations/${assoc.aid}`);
        if (cancelled) return;
        const sy = String(res.data.study_year ?? "");
        const ob = String(res.data.opening_balance ?? "");
        setForm((prev) => ({ ...prev, study_year: sy, opening_balance: ob }));
        setInitialChildFields({ study_year: sy, opening_balance: ob });
      } catch {
        /* leave the two fields blank; parent will refetch on save */
      } finally {
        if (!cancelled) setLoadingBundle(false);
      }
    })();
    return () => { cancelled = true; };
  }, [assoc.aid]);

  const submit = async () => {
    if (!form.name.trim()) {
      setError("Name cannot be empty."); return;
    }
    // Client-side validation for the two child-row fields.
    const sy = Number(form.study_year);
    if (form.study_year !== "" && (!Number.isInteger(sy) || sy < 1900 || sy > 2200)) {
      setError("Plan Year must be a whole number between 1900 and 2200."); return;
    }
    const ob = Number(form.opening_balance);
    if (form.opening_balance !== "" && (!Number.isFinite(ob) || ob < 0)) {
      setError("Opening Fund must be $0.00 or more."); return;
    }
    setSaving(true); setError(null);
    try {
      const patch = {
        name: form.name.trim(),
        tenant_display_id: form.tenant_display_id.trim(),
        address: form.address.trim(),
        unit_count: Number(form.unit_count) || 0,
        tier: form.tier || "pro",
        is_active: !!form.is_active,
      };
      // Only include the two new child-row fields if they actually changed
      // (avoids a needless PlanYear/Scenario write when the drawer opens
      // and closes without touching them).
      if (form.study_year !== initialChildFields.study_year && form.study_year !== "") {
        patch.study_year = sy;
      }
      if (form.opening_balance !== initialChildFields.opening_balance && form.opening_balance !== "") {
        patch.opening_balance = ob;
      }
      const res = await axios.patch(`${API}/admin/associations/${assoc.aid}`, patch);
      onSaved && onSaved(res.data);
      // Slice 5h (2026-02-25) — notify App-level `associations` list so the
      // TopBar picker + Dashboard reflect the rename/display-id/active
      // change without a full page reload. Mirrors the firm-rename fix.
      try {
        window.dispatchEvent(new CustomEvent("ureserve:associations-changed"));
      } catch { /* no-op */ }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 pt-2 border-t border-rule" data-testid={`drawer-edit-form-${assoc.aid}`}>
      {error && (
        <div className="px-2 py-1 border border-coral bg-[#FEF5F2] text-coral text-[11px] mb-2">
          {error}
        </div>
      )}
      <div className="grid grid-cols-2 gap-1.5 text-[11px]">
        <label className="col-span-2 flex flex-col gap-0.5">
          <span className="text-[9.5px] tracking-wide uppercase text-slate">Name</span>
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className="border border-rule px-2 py-1 outline-none focus:border-navy"
            data-testid={`drawer-edit-name-${assoc.aid}`}
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9.5px] tracking-wide uppercase text-slate">Display ID</span>
          <input
            value={form.tenant_display_id}
            onChange={(e) => set("tenant_display_id", e.target.value)}
            className="border border-rule px-2 py-1 outline-none focus:border-navy font-mono"
            data-testid={`drawer-edit-display-id-${assoc.aid}`}
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9.5px] tracking-wide uppercase text-slate">Units</span>
          <input
            type="number" min="0"
            value={form.unit_count}
            onChange={(e) => set("unit_count", e.target.value)}
            className="border border-rule px-2 py-1 outline-none focus:border-navy tabular-nums text-right"
            data-testid={`drawer-edit-units-${assoc.aid}`}
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[9.5px] tracking-wide uppercase text-slate">
            Plan Year {loadingBundle && <em className="text-[9px] normal-case text-slate/70">(loading…)</em>}
          </span>
          <input
            type="number" min="1900" max="2200" step="1"
            value={form.study_year}
            onChange={(e) => set("study_year", e.target.value)}
            className="border border-rule px-2 py-1 outline-none focus:border-navy tabular-nums text-right disabled:bg-zebra disabled:cursor-wait"
            data-testid={`drawer-edit-plan-year-${assoc.aid}`}
            placeholder={loadingBundle ? "loading\u2026" : ""}
            disabled={loadingBundle}
          />
        </label>
        <label className="flex flex-col gap-0.5">
          {/* 2026-03-04 (Dave) — Per-association content tier. */}
          <span className="text-[9.5px] tracking-wide uppercase text-slate">Tier</span>
          <select
            value={form.tier || "pro"}
            onChange={(e) => set("tier", e.target.value)}
            className="border border-rule px-2 py-1 outline-none focus:border-navy"
            data-testid={`drawer-edit-tier-${assoc.aid}`}
          >
            <option value="pro">Pro</option>
            <option value="basic">Basic</option>
          </select>
        </label>
        <label className="col-span-2 flex flex-col gap-0.5">
          <span className="text-[9.5px] tracking-wide uppercase text-slate">
            Opening Fund ($) {loadingBundle && <em className="text-[9px] normal-case text-slate/70">(loading…)</em>}
          </span>
          <input
            type="number" min="0" step="0.01"
            value={form.opening_balance}
            onChange={(e) => set("opening_balance", e.target.value)}
            className="border border-rule px-2 py-1 outline-none focus:border-navy tabular-nums text-right font-mono disabled:bg-zebra disabled:cursor-wait"
            data-testid={`drawer-edit-opening-fund-${assoc.aid}`}
            placeholder={loadingBundle ? "loading\u2026" : ""}
            disabled={loadingBundle}
          />
        </label>
        <label className="col-span-2 flex flex-col gap-0.5">
          <span className="text-[9.5px] tracking-wide uppercase text-slate">Address</span>
          <input
            value={form.address}
            onChange={(e) => set("address", e.target.value)}
            className="border border-rule px-2 py-1 outline-none focus:border-navy"
            data-testid={`drawer-edit-address-${assoc.aid}`}
          />
        </label>
        <label className="col-span-2 flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => set("is_active", e.target.checked)}
            data-testid={`drawer-edit-active-${assoc.aid}`}
          />
          <span className="text-[11px] text-ink">Active</span>
        </label>
      </div>
      <div className="flex items-center justify-end gap-1.5 mt-2">
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-2 py-1 text-[11px] text-slate hover:text-navy"
          data-testid={`drawer-edit-cancel-${assoc.aid}`}
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={saving || !form.name.trim()}
          className="px-2 py-1 text-[11px] font-semibold bg-navy text-white hover:bg-[#12406B] rounded-[3px] disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ boxShadow: saving || !form.name.trim() ? "none" : "0 0 0 2px #EA6A47" }}
          data-testid={`drawer-edit-save-${assoc.aid}`}
        >
          {saving ? <Loader2 size={11} className="animate-spin" /> : "Save"}
        </button>
      </div>
    </div>
  );
}


function AssociationsDrawer({ firm, allFirms, onClose, onReassigned }) {
  // Slice 5 (2026-02-24) — side panel to view/manage a firm's associations
  // and move them to another firm. Platform Admin only (parent screen already
  // gates rendering).
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [movingAid, setMovingAid] = useState(null);
  const [targetTid, setTargetTid] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [editingAid, setEditingAid] = useState(null);   // aid of the row currently being edited inline
  // Step 3 (2026-02-25) — soft/hard delete state for association rows.
  const [busyAid, setBusyAid] = useState(null);           // aid whose async op is in-flight
  const [purging, setPurging] = useState(null);           // {aid, name} of association pending hard purge

  const refresh = useCallback(async () => {
    if (!firm) return;
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/admin/firms/${firm.tenant_id}/associations`);
      // Slice 5d (2026-02-24) — sort by numeric Display ID ascending; non-
      // numeric ids fall to the end, then by name.
      const parseId = (v) => {
        const n = parseInt(String(v ?? "").trim(), 10);
        return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
      };
      const sorted = [...(res.data || [])].sort((x, y) => {
        const dx = parseId(x.tenant_display_id);
        const dy = parseId(y.tenant_display_id);
        if (dx !== dy) return dx - dy;
        return (x.name || "").localeCompare(y.name || "");
      });
      setRows(sorted);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [firm]);

  useEffect(() => { refresh(); }, [refresh]);

  const otherFirms = useMemo(
    () => (allFirms || []).filter((f) => f.tenant_id !== firm?.tenant_id && f.is_active),
    [allFirms, firm],
  );

  const doMove = async (aid) => {
    const tid = targetTid[aid];
    if (!tid) return;
    setMovingAid(aid); setError(null);
    try {
      await axios.patch(`${API}/admin/associations/${aid}/firm`, { tenant_id: tid });
      // Slice 5 follow-up (iteration_35): App.tenants/associations must be
      // re-bootstrapped so the TopBar chip + picker + subsequent moves
      // reflect the new mapping. Matches the TopBar popover's approach.
      window.location.reload();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setMovingAid(null);
    }
  };

  // Step 3 (2026-02-25) — soft toggle: deactivate or reactivate.
  const doSoftToggle = async (aid, currentlyActive) => {
    setBusyAid(aid); setError(null);
    try {
      if (currentlyActive) {
        await axios.delete(`${API}/admin/associations/${aid}`);
      } else {
        await axios.post(`${API}/admin/associations/${aid}/restore`);
      }
      await refresh();
      try {
        window.dispatchEvent(new CustomEvent("ureserve:associations-changed"));
      } catch { /* no-op */ }
      onReassigned && onReassigned();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusyAid(null);
    }
  };

  // Step 3 (2026-02-25) — hard purge: irreversible, requires typed name.
  const doPurge = async (aid, name, confirmName) => {
    setBusyAid(aid); setError(null);
    try {
      await axios.delete(`${API}/admin/associations/${aid}/purge`, {
        data: { confirm_name: confirmName },
      });
      setPurging(null);
      await refresh();
      try {
        window.dispatchEvent(new CustomEvent("ureserve:associations-changed"));
      } catch { /* no-op */ }
      onReassigned && onReassigned();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusyAid(null);
    }
  };

  return (
    <div className="fixed inset-0 z-40" data-testid="associations-drawer">
      <div className="absolute inset-0 bg-ink/30" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-[540px] bg-white border-l-2 border-navy shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b-2" style={{ borderColor: "#1C4E80" }}>
          <div>
            <div className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-navy">
              Associations
            </div>
            <div className="text-[13px] font-semibold text-ink truncate flex items-center gap-1.5">
              <Building2 size={13} className="text-navy shrink-0" />
              {firm?.name || "—"}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {(() => {
              // Slice 5d (2026-02-24) — pre-empt seat-limit before the form
              // opens. seats == 0 / null means unlimited. Compare against
              // the drawer's own live row count (not firm.association_count
              // — that's stale after a reassign until the parent refreshes).
              const seats = Number(firm?.seats || 0);
              const atCapacity = seats > 0 && rows.length >= seats;
              return (
                <button
                  onClick={() => { if (!atCapacity) setShowCreate((v) => !v); }}
                  disabled={atCapacity && !showCreate}
                  title={atCapacity ? `Firm is at capacity (${seats} seat${seats !== 1 ? "s" : ""}). Increase seats first.` : undefined}
                  className={`px-3 py-1 border text-[11px] font-semibold inline-flex items-center gap-1 rounded-[3px] transition-colors ${
                    atCapacity
                      ? "border-rule bg-white text-slate cursor-not-allowed"
                      : showCreate
                        ? "border-navy bg-navy-wash text-navy hover:bg-white"
                        : "border-navy bg-navy text-white hover:bg-[#164066]"
                  }`}
                  data-testid="drawer-add-association-btn"
                >
                  <Plus size={11} strokeWidth={2.4} />
                  {atCapacity ? `At capacity (${rows.length}/${seats})` : showCreate ? "Close" : "Add Association"}
                </button>
              );
            })()}
            <button
              onClick={onClose}
              className="p-1 border border-rule hover:border-navy text-slate hover:text-navy"
              data-testid="associations-drawer-close"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-4 mt-3 px-3 py-2 border border-coral bg-[#FEF5F2] text-coral text-[11px]" data-testid="associations-drawer-error">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-auto px-4 py-3">
          {showCreate && (
            <CreateAssociationForm
              firm={firm}
              onCancel={() => setShowCreate(false)}
              onCreated={async () => {
                // Slice 5e (2026-02-24) — stay in the drawer, refresh the
                // list + notify parent so firms-table association_count is
                // authoritative. Auto-close the create form so the user
                // sees the newly-added row immediately.
                setShowCreate(false);
                await refresh();
                onReassigned && onReassigned();
              }}
            />
          )}
          {loading && (
            <div className="text-center py-8 text-slate text-[11px]">
              <Loader2 className="mx-auto mb-2 animate-spin" size={16} /> Loading…
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="text-center py-8 text-slate text-[11.5px]">
              This firm has no associations.
            </div>
          )}
          {!loading && rows.map((a) => {
            const isMoving = movingAid === a.aid;
            const target = targetTid[a.aid] || "";
            return (
              <div
                key={a.aid}
                className={`border border-rule mb-2 px-3 py-2 ${a.is_active ? "" : "opacity-50"}`}
                data-testid={`drawer-assoc-${a.aid}`}
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-semibold text-ink truncate flex items-center gap-2">
                      <span className="truncate">{a.name}</span>
                      <TierChip tier={a.tier} />
                    </div>
                    <div className="text-[10.5px] text-slate font-mono tabular-nums">
                      ID: {a.tenant_display_id || "—"} · {a.unit_count ?? "—"} units
                      {!a.is_active && <span className="ml-2 text-coral font-semibold">INACTIVE</span>}
                    </div>
                  </div>
                  {/* 3-icon cluster — grouped together and offset from the
                      row's right edge by the Move-button footprint (46px
                      + 6px gap) so the trash icon's right edge lands
                      DIRECTLY ABOVE the "Move to firm" select's right
                      edge on row 2. Internal gap tripled from 6px to
                      18px per Dave 2026-02-08 for better icon breathing
                      room. The trash icon (rightmost) still anchors the
                      cluster's right edge, so alignment with the select
                      below is preserved. */}
                  <div
                    className="flex items-center shrink-0"
                    style={{ marginRight: 55, gap: 18 }}
                    data-testid={`drawer-icon-cluster-${a.aid}`}
                  >
                    <button
                      onClick={() => setEditingAid(editingAid === a.aid ? null : a.aid)}
                      className={`p-1 border ${editingAid === a.aid ? "border-navy bg-navy-wash text-navy" : "border-rule text-slate hover:border-navy hover:text-navy"}`}
                      title="Edit association"
                      data-testid={`drawer-edit-btn-${a.aid}`}
                    >
                      <Pencil size={11} />
                    </button>
                    {/* Step 3 (2026-02-25) — Deactivate / Restore + Delete Permanently. */}
                    <button
                      onClick={() => doSoftToggle(a.aid, a.is_active)}
                      disabled={busyAid === a.aid}
                      className="p-1 border border-rule text-slate hover:border-navy hover:text-navy disabled:opacity-40"
                      title={a.is_active ? "Deactivate association (reversible)" : "Reactivate association"}
                      data-testid={`drawer-soft-toggle-${a.aid}`}
                    >
                      {busyAid === a.aid ? (
                        <Loader2 size={11} className="animate-spin" />
                      ) : a.is_active ? (
                        <PowerOff size={11} />
                      ) : (
                        <Undo2 size={11} />
                      )}
                    </button>
                    <button
                      onClick={() => setPurging({ aid: a.aid, name: a.name })}
                      disabled={busyAid === a.aid}
                      className="p-1 border border-rule text-slate hover:border-coral hover:text-coral disabled:opacity-40"
                      title="Delete association permanently (irreversible)"
                      data-testid={`drawer-purge-btn-${a.aid}`}
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <ArrowRightLeft size={11} className="text-slate shrink-0" />
                  <select
                    value={target}
                    onChange={(e) => setTargetTid((prev) => ({ ...prev, [a.aid]: e.target.value }))}
                    disabled={isMoving || otherFirms.length === 0}
                    className="flex-1 border border-rule bg-white px-2 py-1 text-[11px] outline-none focus:border-navy"
                    data-testid={`drawer-move-target-${a.aid}`}
                  >
                    <option value="">
                      {otherFirms.length === 0 ? "No other active firms" : "Move to firm…"}
                    </option>
                    {otherFirms.map((f) => (
                      <option key={f.tenant_id} value={f.tenant_id}>{f.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => doMove(a.aid)}
                    disabled={!target || isMoving}
                    className="px-2 py-1 text-[11px] font-semibold bg-navy text-white hover:bg-[#12406B] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center shrink-0"
                    style={{ width: 46 }}
                    data-testid={`drawer-move-btn-${a.aid}`}
                  >
                    {isMoving ? <Loader2 size={11} className="animate-spin" /> : "Move"}
                  </button>
                </div>
                {editingAid === a.aid && (
                  <EditAssociationForm
                    assoc={a}
                    onCancel={() => setEditingAid(null)}
                    onSaved={(updated) => {
                      setRows((prev) => prev.map((r) => (r.aid === updated.aid ? { ...r, ...updated } : r)));
                      setEditingAid(null);
                      // Kick off a lightweight refresh so the firm-row count / TopBar
                      // picker labels reflect display-id / active-flag changes.
                      onReassigned && onReassigned();
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="border-t border-rule px-4 py-2 text-[10.5px] text-slate">
          Platform Administrator only. Moves are atomic — all elements, plan years and scenarios follow.
        </div>
      </div>

      {/* Step 3 (2026-02-25) — Hard-purge confirmation modal. */}
      {purging && (
        <PurgeAssociationConfirm
          assoc={purging}
          busy={busyAid === purging.aid}
          onCancel={() => setPurging(null)}
          onConfirm={(typed) => doPurge(purging.aid, purging.name, typed)}
        />
      )}
    </div>
  );
}


function PurgeAssociationConfirm({ assoc, busy, onCancel, onConfirm }) {
  const [typed, setTyped] = useState("");
  const matches = typed.trim() === assoc.name;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      data-testid="purge-assoc-modal"
    >
      <div
        className="absolute inset-0"
        style={{ background: "rgba(15,42,68,0.55)" }}
        onClick={busy ? undefined : onCancel}
      />
      <div className="relative bg-white border-2 border-coral shadow-2xl w-[480px] p-5 rounded-[8px]">
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle size={22} className="text-coral shrink-0 mt-0.5" />
          <div>
            <div className="text-[15px] font-bold text-ink">Permanently delete this association?</div>
            <div className="text-[11.5px] text-slate mt-0.5">This cannot be undone.</div>
          </div>
        </div>
        <div className="text-[11.5px] text-ink mb-3 leading-relaxed">
          You are about to permanently delete <span className="font-semibold text-coral">{assoc.name}</span>.
          Every plan year, every element, every scenario, and every override under this association will be
          erased from the database. There is no restore.
        </div>
        <div className="text-[10.5px] text-slate uppercase tracking-wider font-semibold mb-1">
          Type the exact association name to confirm:
        </div>
        <input
          autoFocus
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={assoc.name}
          disabled={busy}
          className="w-full border border-rule bg-white px-2 py-1.5 text-[12px] font-mono outline-none focus:border-coral"
          data-testid="purge-assoc-typed"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 text-[11.5px] border border-rule text-slate hover:border-navy hover:text-navy disabled:opacity-40"
            data-testid="purge-assoc-cancel"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(typed)}
            disabled={!matches || busy}
            className={`px-3 py-1.5 text-[11.5px] font-semibold bg-coral text-white transition-all
              ${matches && !busy
                ? "ring-2 ring-[#EA6A47]/50 ring-offset-2 shadow-md hover:bg-[#E85A42]"
                : "opacity-40 cursor-not-allowed"}`}
            data-testid="purge-assoc-confirm"
          >
            {busy ? (
              <span className="flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> Deleting…</span>
            ) : "Delete Permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function FirmsScreen({ onScreenChange } = {}) {
  const { user, refreshTenants } = useAuth();
  const canView = isPlatformAdmin(user);

  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [modalMode, setModalMode] = useState(null);   // null | "create" | "edit"
  const [editingFirm, setEditingFirm] = useState(null);
  const [drawerFirm, setDrawerFirm] = useState(null); // Slice 5 — firm whose associations are being viewed/moved
  const [purgingFirm, setPurgingFirm] = useState(null); // Step 4 — {tenant_id, name} pending hard delete
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeError, setPurgeError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 2026-02-25 (Dave) — Wrapped in retryOnReloadHiccup so a
      // uvicorn hot-reload window doesn't paint a scary 404 banner.
      const res = await retryOnReloadHiccup(() =>
        axios.get(`${API}/admin/firms?include_inactive=true`),
      );
      setFirms(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) refresh();
    else setLoading(false);
  }, [canView, refresh]);

  // 2026-03-04 (Dave) — Default alpha sort on Firm Name, ascending.
  // Additional sort columns exposed via header click (name, associations,
  // created, status). Clicking the same header flips direction; clicking
  // a different column resets direction to asc.
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = firms.filter((f) => {
      if (!showInactive && !f.is_active) return false;
      if (!q) return true;
      return (
        (f.name || "").toLowerCase().includes(q) ||
        (f.legal_name || "").toLowerCase().includes(q) ||
        (f.primary_contact || "").toLowerCase().includes(q) ||
        (f.email || "").toLowerCase().includes(q)
      );
    });
    const dir = sortDir === "desc" ? -1 : 1;
    const acc = (f) => {
      switch (sortKey) {
        case "associations": return Number(f.association_count || 0);
        case "users":        return Number(f.user_count || 0);
        case "created":      return f.created_at || "";
        case "status":       return f.is_active ? 1 : 0;
        case "name":
        default:             return (f.name || "").toLowerCase();
      }
    };
    return [...rows].sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      // Stable tiebreaker on name to keep the grid deterministic.
      return (a.name || "").localeCompare(b.name || "") * dir;
    });
  }, [firms, query, showInactive, sortKey, sortDir]);

  const activeCount = firms.filter((f) => f.is_active).length;

  const toggleActive = async (f) => {
    const action = f.is_active ? "deactivate" : "activate";
    try {
      const res = await axios.post(`${API}/admin/firms/${f.tenant_id}/${action}`);
      setFirms((prev) => prev.map((x) => (x.tenant_id === f.tenant_id ? res.data : x)));
      // Slice 5g — cascade to global tenants so the TopBar chip reflects
      // the activation flag change too.
      refreshTenants && refreshTenants();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  };

  const onSaved = (savedFirm) => {
    setFirms((prev) => {
      const idx = prev.findIndex((x) => x.tenant_id === savedFirm.tenant_id);
      if (idx < 0) return [savedFirm, ...prev];
      const next = [...prev];
      next[idx] = savedFirm;
      return next;
    });
    // Slice 5g (2026-02-24) — the global `tenants` list (feeds TopBar chip +
    // Dashboard picker labels) is cached at bootstrap. After a firm rename
    // or edit, refresh it so the change propagates everywhere without
    // needing a full page reload.
    refreshTenants && refreshTenants();
  };

  // Step 4 (2026-07-31) — Hard delete a firm. Backend rejects with 409 if
  // any associations still belong to the firm; we surface the error text
  // verbatim inside the modal so the CPA sees exactly what to clean up.
  const doPurgeFirm = async (tenant_id, name, typed) => {
    setPurgeBusy(true);
    setPurgeError(null);
    try {
      await axios.delete(`${API}/admin/firms/${tenant_id}`, {
        data: { confirm_name: typed },
      });
      setFirms((prev) => prev.filter((x) => x.tenant_id !== tenant_id));
      refreshTenants && refreshTenants();
      setPurgingFirm(null);
    } catch (err) {
      setPurgeError(err?.response?.data?.detail || err.message || "Delete failed.");
    } finally {
      setPurgeBusy(false);
    }
  };

  if (!canView) {
    return (
      <div className="panel-compact flex flex-col flex-1 overflow-hidden" data-testid="screen-firms">
        <div
          className="flex items-center justify-between mb-1.5 pb-1 border-b-2"
          style={{ borderColor: "#1C4E80" }}
        >
          <div className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-navy">
            Firms
          </div>
        </div>
        <div className="p-8 text-center text-slate">
          <Building2 size={32} className="mx-auto text-slate mb-2 opacity-40" />
          <div className="text-[13px] font-semibold text-ink mb-1">Platform Administrator only</div>
          <div className="text-[11px]">You need Platform Administrator rights to view or manage Firms.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel-compact flex flex-col flex-1 overflow-hidden" data-testid="screen-firms">
      <div
        className="flex items-center justify-between mb-1.5 pb-1 border-b-2 gap-3"
        style={{ borderColor: "#1C4E80" }}
      >
        <div className="shrink-0">
          <div className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-navy">
            Firms
          </div>
          <div className="text-[10.5px] text-slate font-mono tabular-nums">
            {filtered.length} of {firms.length} firms
            {" "}· <span className="text-ink font-semibold">{activeCount}</span> active
            {" "}· <span className="text-ink font-semibold">{firms.length - activeCount}</span> inactive
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center border border-rule bg-white px-2 py-1">
            <Search size={12} className="text-slate mr-1.5" />
            <input
              data-testid="firms-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search firms…"
              className="outline-none text-[12px] w-[180px] bg-transparent"
            />
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-slate cursor-pointer select-none">
            <input
              data-testid="firms-show-inactive"
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="accent-navy"
            />
            Show inactive
          </label>
          <button
            onClick={() => { setEditingFirm(null); setModalMode("create"); }}
            className="flex items-center gap-1 bg-navy text-white hover:bg-[#12406B] px-2 py-1 text-[11px] font-semibold"
            data-testid="firm-add-btn"
          >
            <Plus size={12} /> Add Firm
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-2 px-3 py-2 border border-coral bg-[#FEF5F2] text-coral text-[11px]" data-testid="firms-error">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <table className="w-full text-[11.5px]" data-testid="firms-table">
          <thead className="sticky top-0 z-10 bg-navy-wash border-b-2 border-navy" style={{ boxShadow: "0 2px 0 #1C4E80" }}>
            <tr className="text-[9.5px] tracking-[0.12em] uppercase text-navy font-semibold">
              <Th sortKey="name" activeKey={sortKey} dir={sortDir} onSort={toggleSort}>Firm Name</Th>
              <Th>Legal Name</Th>
              <Th>Primary Contact</Th>
              <Th>Email</Th>
              <Th right>Seats</Th>
              <Th right sortKey="associations" activeKey={sortKey} dir={sortDir} onSort={toggleSort}>Associations</Th>
              {/* 2026-03-08 (Dave) — Users rollup. Distinct active
                  users with a client-scope membership on any
                  association under the firm. Matches the per-aid
                  count on Admin → Associations, aggregated to the
                  firm level. */}
              <Th right sortKey="users" activeKey={sortKey} dir={sortDir} onSort={toggleSort}>Users</Th>
              <Th sortKey="created" activeKey={sortKey} dir={sortDir} onSort={toggleSort}>Created</Th>
              <Th center sortKey="status" activeKey={sortKey} dir={sortDir} onSort={toggleSort}>Status</Th>
              <Th center>Actions</Th>
            </tr>
          </thead>
          <tbody className="font-mono tabular-nums">
            {loading && (
              <tr><td colSpan={10} className="text-center py-8 text-slate">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={10} className="text-center py-8 text-slate">No firms match your filters.</td></tr>
            )}
            {!loading && filtered.map((f) => (
              <tr
                key={f.tenant_id}
                data-testid={`firm-row-${f.tenant_id}`}
                className={`border-b border-rule hover:bg-navy-wash/40 ${!f.is_active ? "opacity-50" : ""}`}
              >
                <Td>
                  <div className="flex items-center gap-1.5">
                    <Building2 size={11} className="text-navy shrink-0" />
                    <span className="font-semibold text-ink">{f.name}</span>
                  </div>
                </Td>
                <Td className="text-slate">{f.legal_name || "—"}</Td>
                <Td>{f.primary_contact || "—"}</Td>
                <Td className="text-slate">{f.email || "—"}</Td>
                <Td right>{f.seats == null || f.seats === "" ? "—" : Number(f.seats).toLocaleString("en-US")}</Td>
                <Td right className="font-semibold">
                  <button
                    type="button"
                    onClick={() => setDrawerFirm(f)}
                    className="inline-flex items-center gap-1 text-navy px-2 py-2 -my-1 rounded-[3px] active:bg-navy-wash"
                    style={{ touchAction: "manipulation", minHeight: 44, minWidth: 44 }}
                    title="View / reassign associations"
                    data-testid={`firm-assocs-${f.tenant_id}`}
                  >
                    {Number(f.association_count || 0).toLocaleString("en-US")}
                    <ChevronRight size={14} />
                  </button>
                </Td>
                {/* 2026-03-08 (Dave) — Users rollup cell. Distinct
                    active users across all associations owned by the
                    firm. Clickable — drills through to Admin → Users
                    pre-filtered to this firm (handoff via
                    sessionStorage + onScreenChange). */}
                <Td right className="font-semibold" data-testid={`firm-users-${f.tenant_id}`}>
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        sessionStorage.setItem("ureserve:users-filter-tenant-id", f.tenant_id);
                        sessionStorage.setItem("ureserve:users-filter-firm-name", f.name || "");
                        // Clear any lingering association-scope handoff
                        // so the two drill-throughs never stack.
                        sessionStorage.removeItem("ureserve:users-filter-aid");
                        sessionStorage.removeItem("ureserve:users-filter-name");
                      } catch { /* sessionStorage disabled — filter won't preload */ }
                      if (typeof onScreenChange === "function") onScreenChange("Users");
                    }}
                    className="text-navy hover:text-coral hover:underline underline-offset-2 transition-colors disabled:opacity-40 disabled:no-underline disabled:cursor-default"
                    disabled={!Number(f.user_count || 0)}
                    title={Number(f.user_count || 0) ? `View the ${Number(f.user_count)} user${Number(f.user_count) === 1 ? "" : "s"} on ${f.name}` : "No users on this firm yet"}
                    data-testid={`firm-users-link-${f.tenant_id}`}
                  >
                    {Number(f.user_count || 0).toLocaleString("en-US")}
                  </button>
                </Td>
                <Td className="text-slate">{fmtDate(f.created_at)}</Td>
                <Td center>
                  <span
                    className={`inline-block px-2 py-0.5 text-[9.5px] tracking-[0.1em] uppercase font-bold border ${
                      f.is_active
                        ? "border-navy text-navy bg-navy-wash"
                        : "border-slate text-slate bg-white"
                    }`}
                  >
                    {f.is_active ? "Active" : "Inactive"}
                  </span>
                </Td>
                <Td center>
                  {/* Icon cluster mirrors the drawer's 3-icon group:
                      18px gap and all three icons share the pencil's
                      slate → navy hover color for visual consistency
                      (Dave 2026-02-08). */}
                  <div
                    className="flex items-center justify-center"
                    style={{ gap: 18 }}
                  >
                    <button
                      onClick={() => { setEditingFirm(f); setModalMode("edit"); }}
                      className="p-1 border border-rule text-slate hover:border-navy hover:text-navy"
                      title="Edit"
                      data-testid={`firm-edit-${f.tenant_id}`}
                    >
                      <Pencil size={11} />
                    </button>
                    <button
                      onClick={() => toggleActive(f)}
                      className="p-1 border border-rule text-slate hover:border-navy hover:text-navy"
                      title={f.is_active ? "Deactivate" : "Reactivate"}
                      data-testid={`firm-toggle-${f.tenant_id}`}
                    >
                      {f.is_active ? <PowerOff size={11} /> : <Power size={11} />}
                    </button>
                    {/* Step 4 (2026-07-31) — hard delete a firm. Backend
                        blocks with 409 if any associations still belong
                        to the firm. */}
                    <button
                      onClick={() => { setPurgeError(null); setPurgingFirm(f); }}
                      className="p-1 border border-rule text-slate hover:border-navy hover:text-navy"
                      title="Permanent Delete"
                      data-testid={`firm-purge-${f.tenant_id}`}
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalMode && (
        <FirmModal
          mode={modalMode}
          initial={editingFirm}
          onClose={() => setModalMode(null)}
          onSaved={onSaved}
        />
      )}

      {drawerFirm && (
        <AssociationsDrawer
          firm={drawerFirm}
          allFirms={firms}
          onClose={() => setDrawerFirm(null)}
          onReassigned={refresh}
        />
      )}

      {purgingFirm && (
        <PurgeFirmConfirm
          firm={purgingFirm}
          busy={purgeBusy}
          error={purgeError}
          onCancel={() => { if (!purgeBusy) { setPurgingFirm(null); setPurgeError(null); } }}
          onConfirm={(typed) => doPurgeFirm(purgingFirm.tenant_id, purgingFirm.name, typed)}
        />
      )}
    </div>
  );
}


// Step 4 (2026-07-31) — Hard-delete confirmation modal for firms.
// Requires typing the exact firm name. Surfaces the backend 409
// (blocked-by-associations) error text inline so the CPA sees exactly
// what still needs cleaning up before the firm can be deleted.
function PurgeFirmConfirm({ firm, busy, error, onCancel, onConfirm }) {
  const [typed, setTyped] = useState("");
  const matches = typed.trim() === firm.name;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      data-testid="purge-firm-modal"
    >
      <div
        className="absolute inset-0"
        style={{ background: "rgba(15,42,68,0.55)" }}
        onClick={busy ? undefined : onCancel}
      />
      <div className="relative bg-white border-2 border-coral shadow-2xl w-[520px] p-5 rounded-[8px]">
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle size={22} className="text-coral shrink-0 mt-0.5" />
          <div>
            <div className="text-[15px] font-bold text-ink">Permanently delete this firm?</div>
            <div className="text-[11.5px] text-slate mt-0.5">This cannot be undone.</div>
          </div>
        </div>
        <div className="text-[11.5px] text-ink mb-3 leading-relaxed">
          You are about to permanently delete <span className="font-semibold text-coral">{firm.name}</span>.
          The firm record itself will be erased. This action is BLOCKED if any associations still belong
          to this firm — clean those up first (open the firm&apos;s drawer and delete each association).
        </div>
        <div className="text-[10.5px] text-slate uppercase tracking-wider font-semibold mb-1">
          Type the exact firm name to confirm:
        </div>
        <input
          autoFocus
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={firm.name}
          disabled={busy}
          className="w-full border border-rule bg-white px-2 py-1.5 text-[12px] font-mono outline-none focus:border-coral"
          data-testid="purge-firm-typed"
        />
        {error && (
          <div
            className="mt-3 px-2.5 py-2 border border-coral bg-[#FEF5F2] text-coral text-[11px] leading-snug"
            data-testid="purge-firm-error"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 text-[11.5px] border border-rule text-slate hover:border-navy hover:text-navy disabled:opacity-40"
            data-testid="purge-firm-cancel"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(typed)}
            disabled={!matches || busy}
            className={`px-3 py-1.5 text-[11.5px] font-semibold bg-coral text-white transition-all
              ${matches && !busy
                ? "ring-2 ring-[#EA6A47]/50 ring-offset-2 shadow-md hover:bg-[#E85A42]"
                : "opacity-40 cursor-not-allowed"}`}
            data-testid="purge-firm-confirm"
          >
            {busy ? (
              <span className="flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> Deleting…</span>
            ) : "Delete Permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Th({ children, right, center, sortKey, activeKey, dir, onSort }) {
  // 2026-08-04 (Dave iPad "T" bug) — defensive hardening:
  // • whitespace-nowrap so the header text never wraps or truncates weirdly
  // • title= tooltip so hover shows the full label (accessibility bonus)
  // • explicit -webkit-user-select: none so iOS Safari's text-select
  //   gesture doesn't grab stray characters from the header row
  // 2026-03-04 (Dave) — Optional sort props: passing `sortKey` turns
  // the header into a click-to-sort button and renders a caret when it
  // matches `activeKey`.
  const label = typeof children === "string" ? children : undefined;
  const isSortable = !!sortKey && typeof onSort === "function";
  const isActive = isSortable && sortKey === activeKey;
  const caret = isActive ? (dir === "desc" ? "▼" : "▲") : "";
  return (
    <th
      title={label}
      onClick={isSortable ? () => onSort(sortKey) : undefined}
      className={`px-2 py-1.5 border-b border-rule font-bold whitespace-nowrap select-none ${
        right ? "text-right" : center ? "text-center" : "text-left"
      } ${isSortable ? "cursor-pointer hover:text-coral" : ""}`}
      data-testid={isSortable ? `firms-sort-${sortKey}` : undefined}
    >
      {children}
      {caret && <span className="ml-1 text-coral">{caret}</span>}
    </th>
  );
}

function Td({ children, right, center, className = "" }) {
  return (
    <td
      className={`px-2 py-1.5 ${
        right ? "text-right" : center ? "text-center" : "text-left"
      } ${className}`}
    >
      {children}
    </td>
  );
}
